import { Typography, Grid, Skeleton } from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import { type ApprovalDetailDataObject } from '../types';
import { get } from "lodash";
import { DocumentsContainer } from 'modules/cbre-document-file-upload-download';
import DescriptionIcon from '@mui/icons-material/Description';
import service from 'util/apiService';

interface ApprovalDetailsProps {
    mode: 'mini' | 'large';
    apiData: any;
    selectedApproval?:any;
    baseUrl:string
}

type detail = ApprovalDetailDataObject["data"]["sectionDetails"][0]["additionalFields"][0];

const ApprovalDetailsView: React.FC<ApprovalDetailsProps> = ({ mode, apiData, selectedApproval, baseUrl }) => {
    const [showAttachments, setShowAttachments] = React.useState(false);
    const [approvalQuoteAttachments, setApprovalQuoteAttachments] = React.useState<any[]>([]);
    const [attachmentsLoading, setAttachmentsLoading] = React.useState(false);
    const { approvalDetails, cdfDetails } = apiData || {};
    const fieldSchema: Array<ApprovalDetailDataObject["data"]> = cdfDetails?.fieldSchema || [];

    React.useEffect(() => {
        const fetchWorkOrderAttachments = async () => {
            if (!approvalDetails?.workOrderIdSource) {
                setApprovalQuoteAttachments([]);
                return;
            }
            setAttachmentsLoading(true);
            try {
                const response = await service.get(
                    `${baseUrl.replace(/\/$/, '')}/work-requests/work-order/attachments?workOrderIdSource=${approvalDetails.workOrderIdSource}${approvalDetails.site ? `&site=${approvalDetails.site}` : ''}&isApprovalAttachment=true`
                );
                if (response.data?.approvalQuote) {
                    setApprovalQuoteAttachments(response.data.approvalQuote);
                }
            } catch (error) {
                console.error('Failed to fetch work order attachments:', error);
            } finally {
                setAttachmentsLoading(false);
            }
        };
        fetchWorkOrderAttachments();
    }, [approvalDetails?.workOrderIdSource, approvalDetails?.site, baseUrl]);

    const handleDownloadAttachment = async (doc: any) => {
        if (!doc?.documentPath) {
            console.error('documentPath is missing in document:', doc);
            return;
        }
        try {
            const fileName = doc.documentPath.split('/blobs/').pop() || doc.documentPath.split('/').pop() || doc.fileName;
            const apiUrl = `${baseUrl.replace(/\/$/, '')}/documents/work-order/download`;
            const response = await (service as any).service.request({
                method: 'POST',
                url: `${apiUrl}${approvalDetails?.site ? `?site=${approvalDetails.site}` : ''}`,
                responseType: 'blob',
                data: {
                    workOrderId: approvalDetails?.workOrderId ? parseInt(approvalDetails.workOrderId) : undefined,
                    documentPath: doc.documentPath,
                    fileName: fileName,
                },
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', doc.fileName || fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Attachment download failed:', error);
        }
    };
    const handleAssetId = (detail: detail) => {
        if (selectedApproval?.approvalFlow?.sourceConfig?.approvalType === 'bae9f55f-9e2a-455d-9b2c-7bf5b42e8735') {
            return <div><Typography>{ get(approvalDetails, detail.accessorKey) }</Typography></div>;
        }
        
        const id = get(approvalDetails, detail.paramVariableName);
        const url =`${window.location.origin}${detail.url}${id}`
        const additionalAttr:any = {};
        if((detail as any).targetWindow === "NEW") additionalAttr.target="_blank"
        return <div><a className='asset-id-text' href={id ? url: "#"} {...additionalAttr}><Typography>{ get(approvalDetails, detail.accessorKey) }</Typography></a> </div>
    }

   

    const handleAttachment = (detail: detail) => {
        const allWorkOrderDocs = [...approvalQuoteAttachments];
        return <Box className="attachments-list">
             <DocumentsContainer baseUrl={baseUrl} documentId = {selectedApproval?.approvalFlow?.documentId} hideUpload={true} hideDelete={true} getAllFiles={(data)=> {
                if(Object.keys(data).length === 0){
                   return setShowAttachments(false);
                }
                setShowAttachments(true);
             }
             } />
             {attachmentsLoading ? (
                <Skeleton variant="text" width="200px" height={20} />
             ) : allWorkOrderDocs.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {allWorkOrderDocs.map((attachment: any, index: number) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.7 },
                            }}
                            onClick={() => handleDownloadAttachment(attachment)}
                        >
                            <DescriptionIcon sx={{ fontSize: 20, color: '#666' }} />
                            <Typography variant="body2" sx={{ color: '#1976d2' }}>
                                {attachment.fileName}
                            </Typography>
                        </Box>
                    ))}
                </Box>
             ) : null}
        </Box>
    }

    const handleHtmlBlob = (detail: detail) => {
      const content = get(approvalDetails, detail.accessorKey);
      if (!content) {
        return <Typography></Typography>;
      }
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    };

    const renderData = (detail: detail, approvalDetails:any) => {
        //test
        if (detail.isNavigation) return handleAssetId(detail)
        if (detail.isAttachment) return handleAttachment(detail)
        if (detail.type==='html') return handleHtmlBlob(detail)

            //console.log(detail, approvalDetails, 'approvalDetails')
        return <Typography>{ get(approvalDetails, detail.accessorKey) }</Typography>
    }


    return (<>{fieldSchema.map(field => {
        return field.sectionDetails.map((item) => {
            return <Box className="details-section" key={item.sectionName}>
                <Typography variant="h6">{item.sectionName}</Typography>
                {
                    item.additionalFields.map((detail) => {

                        return <Grid container className="detail-item" key={detail.fieldName}>
                            <Grid xs={12}>
                                <Typography variant="body2" className='detail-item-title'>{detail.isAttachment ? (showAttachments || approvalQuoteAttachments.length > 0) ? detail.displayName : "" : detail.displayName}</Typography>
                            </Grid>
                            <Grid xs={12}>
                                {renderData(detail, approvalDetails)}
                            </Grid>
                        </Grid>
                    }
                    )
                }
            </Box>
        })
    })}</>)


};

export default ApprovalDetailsView;